import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatAddress, formatETH } from '../../lib/utils';
import { Coins, User, Trophy, Shield, Copy, Check } from 'lucide-react';

export function BetStakesCard({ bet, totalPool }) {
    const { t } = useTranslation();
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Coins className="w-5 h-5 text-[#00FF66]" />
                    {t('betDetail.stakesOverview')}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="text-xs text-white/40 mb-1">{t('betDetail.creatorStake')}</div>
                        <div className="font-mono text-lg text-white font-bold">{formatETH(bet.creator_stake)} ETH</div>
                    </div>
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="text-xs text-white/40 mb-1">{t('betDetail.takerStake')}</div>
                        <div className="font-mono text-lg text-[#FFD700] font-bold">{formatETH(bet.taker_stake)} ETH</div>
                    </div>
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="text-xs text-white/40 mb-1">{t('betDetail.totalPool')}</div>
                        <div className="font-mono text-lg text-white font-bold">{formatETH(totalPool)} ETH</div>
                    </div>
                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                        <div className="text-xs text-[#00FF66] mb-1">{t('betCard.odds')}</div>
                        <div className="font-mono text-lg text-[#00FF66] font-bold">{bet.odds_display?.toFixed(2)}x</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export function BetParticipantsCard({ bet, isCreator, isTaker }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const copyAddr = () => {
        navigator.clipboard.writeText(bet.creator_address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-[#00FF66]" />
                    {t('betDetail.participants')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-white/[0.03] border border-white/5 flex items-center justify-between">
                    <div>
                        <div className="text-xs text-white/40 mb-1">{t('betDetail.creatorBettingFor')}</div>
                        <div className="font-mono text-white flex items-center gap-2">
                            {formatAddress(bet.creator_address)}
                            {isCreator && <Badge className="bg-[#00FF66]/10 text-[#00FF66] text-xs border-0">{t('common.you')}</Badge>}
                            <button onClick={copyAddr} className="text-white/40 hover:text-white">
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">{t('betCard.stake')}</div>
                        <div className="font-mono text-white font-semibold">{formatETH(bet.creator_stake)} ETH</div>
                    </div>
                </div>

                <div className={`p-4 rounded-lg border ${bet.taker_address ? 'bg-white/[0.03] border-white/5' : 'bg-[#00FF66]/5 border-[#00FF66]/20 border-dashed'} flex items-center justify-between`}>
                    <div>
                        <div className="text-xs text-white/40 mb-1">{t('betDetail.takerBettingAgainst')}</div>
                        {bet.taker_address ? (
                            <div className="font-mono text-white flex items-center gap-2">
                                {formatAddress(bet.taker_address)}
                                {isTaker && <Badge className="bg-[#6F3FCA]/20 text-[#9B6FE8] text-xs border-0">{t('common.you')}</Badge>}
                            </div>
                        ) : (
                            <div className="text-[#00FF66]">{t('betDetail.waitingForTaker')}</div>
                        )}
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">{t('betDetail.stakeRequired')}</div>
                        <div className="font-mono text-white font-semibold">{formatETH(bet.taker_stake)} ETH</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export function BetWinnerCard({ bet }) {
    const { t } = useTranslation();
    if (bet.status !== 'resolved' || !bet.winner_address) return null;
    return (
        <Card className="bg-[#00FF66]/5 border-[#00FF66]/20">
            <CardContent className="p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#00FF66]/20 flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-[#00FF66]" />
                        </div>
                        <div>
                            <div className="text-sm text-[#00FF66] mb-1">{t('betDetail.winner')}</div>
                            <div className="font-mono text-white text-lg">{formatAddress(bet.winner_address)}</div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-white/50 mb-1">{t('betDetail.payout')}</div>
                        <div className="font-mono text-2xl text-[#00FF66] font-bold">+{formatETH(bet.payout_amount)} ETH</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export function BetTransactionHistory({ bet }) {
    const { t } = useTranslation();
    if (!bet.tx_hash_create && !bet.tx_hash_accept && !bet.tx_hash_resolve) return null;
    const rows = [
        { hash: bet.tx_hash_create, labelKey: 'betDetail.createTx' },
        { hash: bet.tx_hash_accept, labelKey: 'betDetail.acceptTx' },
        { hash: bet.tx_hash_resolve, labelKey: 'betDetail.resolveTx' },
    ].filter(r => r.hash);
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader>
                <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#00FF66]" />
                    {t('betDetail.transactionHistory')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {rows.map(r => (
                    <div key={r.labelKey} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg">
                        <span className="text-sm text-white/50">{t(r.labelKey)}</span>
                        <span className="font-mono text-xs text-[#00FF66]">{r.hash.slice(0, 10)}...{r.hash.slice(-8)}</span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
